import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Clock, CheckCircle2 } from "lucide-react";

interface ConcludeMeetingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConclude: (endTime: string) => void;
  startTime?: string;
}

export function ConcludeMeetingModal({
  open,
  onOpenChange,
  onConclude,
  startTime
}: ConcludeMeetingModalProps) {
  const [endTime, setEndTime] = useState("");

  // Update end time to current time when modal opens
  useEffect(() => {
    if (open) {
      const now = new Date();
      setEndTime(now.toTimeString().slice(0, 5));
    }
  }, [open]);

  const formatTime12Hour = (time24: string) => {
    if (!time24) return '';
    try {
      const [hours, minutes] = time24.split(':').map(Number);
      const period = hours >= 12 ? 'PM' : 'AM';
      const hours12 = hours % 12 || 12;
      return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
    } catch {
      return time24;
    }
  };

  const handleConclude = () => {
    onConclude(endTime);
    onOpenChange(false);
  };

  const calculateDuration = () => {
    if (!startTime || !endTime) return null;
    
    try {
      const [startHour, startMin] = startTime.split(':').map(Number);
      const [endHour, endMin] = endTime.split(':').map(Number);
      
      const startMinutes = startHour * 60 + startMin;
      const endMinutes = endHour * 60 + endMin;
      
      let duration = endMinutes - startMinutes;
      if (duration < 0) duration += 24 * 60; // Handle day overflow
      
      const hours = Math.floor(duration / 60);
      const minutes = duration % 60;
      
      if (hours > 0) {
        return `${hours} hour${hours !== 1 ? 's' : ''} ${minutes} minute${minutes !== 1 ? 's' : ''}`;
      }
      return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
    } catch {
      return null;
    }
  };

  const duration = calculateDuration();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            Conclude Meeting
          </DialogTitle>
          <DialogDescription>
            Enter the meeting end time to finalize and save the minutes as a draft for review.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="end-time">Meeting End Time</Label>
            <Input
              id="end-time"
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
            />
          </div>

          {startTime && duration && (
            <div className="bg-muted rounded-lg p-3 space-y-1">
              <div className="flex items-center gap-2 text-sm">
                <Clock className="h-4 w-4" />
                <span className="font-medium">Meeting Duration</span>
              </div>
              <p className="text-sm text-muted-foreground pl-6">
                Started at {formatTime12Hour(startTime)} • Ending at {formatTime12Hour(endTime)}
              </p>
              <p className="text-sm font-semibold pl-6">
                Total duration: {duration}
              </p>
            </div>
          )}

          <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <p className="text-sm text-blue-900 dark:text-blue-100">
              <strong>Next steps:</strong> After concluding, the meeting will be saved as a draft. 
              You can then review, edit, and certify the minutes before generating final documents.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleConclude}>
            Conclude Meeting
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
